import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import type { IncidentAiChatPlatformPolicy, IncidentAiChatPlatformTenantPolicy } from '../../types';

const POLICY_QUERY_KEY = ['incident-ai-chat-platform-policy'];
const MAX_LIMIT = 2147483647;
const WHOLE_NUMBER = /^\d+$/;

const PRIMARY_BUTTON =
  'whitespace-nowrap rounded-md bg-primary-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60';
const SECONDARY_BUTTON =
  'whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700';
const NUMBER_INPUT =
  'rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

interface ParsedLimit {
  isValid: boolean;
  /** null = empty input, which means "inherit the platform default" for tenant overrides. */
  value: number | null;
}

function parseLimit(raw: string, allowEmpty: boolean): ParsedLimit {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { isValid: allowEmpty, value: null };
  }

  if (!WHOLE_NUMBER.test(trimmed)) {
    return { isValid: false, value: null };
  }

  const value = Number(trimmed);
  return value <= MAX_LIMIT ? { isValid: true, value } : { isValid: false, value: null };
}

function getErrorMessage(error: unknown): string {
  const apiError = error as { response?: { data?: { title?: string; detail?: string; message?: string } }; message?: string };
  return apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'The request failed.';
}

interface TenantPolicyRowProps {
  tenant: IncidentAiChatPlatformTenantPolicy;
  platformDefaultLimit: number;
  draft: string | undefined;
  isBusy: boolean;
  isSaving: boolean;
  onDraftChange: (value: string) => void;
  onSave: (limit: number | null) => void;
}

function TenantPolicyRow({
  tenant,
  platformDefaultLimit,
  draft,
  isBusy,
  isSaving,
  onDraftChange,
  onSave,
}: TenantPolicyRowProps) {
  const storedText = tenant.overrideLimit === null ? '' : String(tenant.overrideLimit);
  const inputValue = draft ?? storedText;
  const parsed = parseLimit(inputValue, true);
  const isDirty = draft !== undefined && draft.trim() !== storedText;
  const isCapped = tenant.defaultLimit > tenant.effectiveDefaultLimit;
  const errorId = `incident-chat-override-error-${tenant.tenantId}`;

  return (
    <tr>
      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
        {tenant.tenantName}
        {!tenant.isActive ? (
          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            Inactive
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{tenant.tier}</td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{tenant.tierCeiling}</td>
      <td className="px-3 py-2">
        <input
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          value={inputValue}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder={`Inherit (${platformDefaultLimit})`}
          aria-label={`Incident AI chat override for ${tenant.tenantName}`}
          aria-invalid={!parsed.isValid}
          aria-describedby={parsed.isValid ? undefined : errorId}
          className={`w-36 ${NUMBER_INPUT}`}
          disabled={isBusy}
        />
        {!parsed.isValid ? (
          <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400">
            Enter a whole number of 0 or more, or leave empty to inherit.
          </p>
        ) : null}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-gray-900 dark:text-gray-100">{tenant.effectiveDefaultLimit}</span>
          <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
            {tenant.inheritsPlatformDefault ? 'platform default' : 'tenant override'}
          </span>
          {isCapped ? (
            <span
              className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              title={`The configured value of ${tenant.defaultLimit} is capped by the ${tenant.tier} tier ceiling.`}
            >
              capped by {tenant.tier} ceiling ({tenant.tierCeiling})
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={isBusy || !parsed.isValid || !isDirty}
            onClick={() => onSave(parsed.value)}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={isBusy || tenant.overrideLimit === null}
            onClick={() => onSave(null)}
            aria-label={`Use platform default for ${tenant.tenantName}`}
          >
            Use platform default
          </button>
        </div>
      </td>
    </tr>
  );
}

export function IncidentAiChatPolicySection() {
  const queryClient = useQueryClient();
  const [defaultDraft, setDefaultDraft] = useState<string | null>(null);
  const [overrideDrafts, setOverrideDrafts] = useState<Record<string, string>>({});
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const policyQuery = useQuery({
    queryKey: POLICY_QUERY_KEY,
    queryFn: () => apiClient.getIncidentAiChatPlatformPolicy(),
  });

  const savePlatformDefaultMutation = useMutation({
    mutationFn: (limit: number) => apiClient.updateIncidentAiChatPlatformDefault(limit),
    onMutate: () => {
      setStatusMessage(null);
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(POLICY_QUERY_KEY, updated);
      setDefaultDraft(null);
      setStatusMessage(`Platform default saved: ${updated.platformDefaultLimit} answers per technician per incident.`);
      await queryClient.invalidateQueries({ queryKey: POLICY_QUERY_KEY });
    },
  });

  const saveOverrideMutation = useMutation({
    mutationFn: (args: { tenantId: string; limit: number | null }) =>
      apiClient.updateIncidentAiChatPlatformTenantOverride(args.tenantId, args.limit),
    onMutate: () => {
      setStatusMessage(null);
    },
    onSuccess: async (updatedTenant) => {
      queryClient.setQueryData<IncidentAiChatPlatformPolicy>(POLICY_QUERY_KEY, (current) =>
        current
          ? {
              ...current,
              tenants: current.tenants.map((tenant) =>
                tenant.tenantId === updatedTenant.tenantId ? updatedTenant : tenant),
            }
          : current);
      setOverrideDrafts((current) => {
        const next = { ...current };
        delete next[updatedTenant.tenantId];
        return next;
      });
      setStatusMessage(
        updatedTenant.inheritsPlatformDefault
          ? `${updatedTenant.tenantName} now uses the platform default.`
          : `Override saved for ${updatedTenant.tenantName}: ${updatedTenant.defaultLimit} answers per technician per incident.`,
      );
      await queryClient.invalidateQueries({ queryKey: POLICY_QUERY_KEY });
    },
  });

  const policy = policyQuery.data;
  const defaultInput = defaultDraft ?? (policy ? String(policy.platformDefaultLimit) : '');
  const parsedDefault = parseLimit(defaultInput, false);
  const inheritingCount = policy?.tenants.filter((tenant) => tenant.inheritsPlatformDefault).length ?? 0;
  const isBusy = savePlatformDefaultMutation.isPending || saveOverrideMutation.isPending;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Incident AI chat default policy</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        How many successful AI chat answers each technician can get per incident. The platform default applies to every
        tenant that has no override of its own. A tenant override replaces the platform default for that tenant only.
        Either value is always capped by the tenant&apos;s tier ceiling (see above), and technician overrides set by a
        tenant admin still win over the tenant default.
      </p>

      {policyQuery.isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading incident AI chat policy…</p>
      ) : null}

      {policyQuery.isError ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40" role="alert">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load the incident AI chat policy.</p>
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">{getErrorMessage(policyQuery.error)}</p>
          <button type="button" className={`mt-2 ${SECONDARY_BUTTON}`} onClick={() => void policyQuery.refetch()}>
            Retry
          </button>
        </div>
      ) : null}

      {policy ? (
        <>
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <label
              htmlFor="incident-chat-platform-default"
              className="block text-sm font-medium text-gray-800 dark:text-gray-200"
            >
              Platform default (answers per technician per incident)
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                id="incident-chat-platform-default"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={defaultInput}
                onChange={(event) => setDefaultDraft(event.target.value)}
                aria-invalid={!parsedDefault.isValid}
                aria-describedby="incident-chat-platform-default-help"
                className={`w-32 ${NUMBER_INPUT}`}
                disabled={isBusy}
              />
              <button
                type="button"
                className={PRIMARY_BUTTON}
                disabled={isBusy || !parsedDefault.isValid || parsedDefault.value === null}
                onClick={() => {
                  if (parsedDefault.isValid && parsedDefault.value !== null) {
                    savePlatformDefaultMutation.mutate(parsedDefault.value);
                  }
                }}
              >
                {savePlatformDefaultMutation.isPending ? 'Saving…' : 'Save platform default'}
              </button>
            </div>
            <p id="incident-chat-platform-default-help" className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {parsedDefault.isValid
                ? `Currently ${policy.platformDefaultLimit}. ${inheritingCount} of ${policy.tenants.length} tenants use this value.`
                : 'Enter a whole number of 0 or more.'}
            </p>
            {savePlatformDefaultMutation.isError ? (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
                {getErrorMessage(savePlatformDefaultMutation.error)}
              </p>
            ) : null}
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">Incident AI chat policy per tenant</caption>
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Tenant</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Tier</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Tier ceiling</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Tenant override</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Effective default</th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {policy.tenants.map((tenant) => (
                  <TenantPolicyRow
                    key={tenant.tenantId}
                    tenant={tenant}
                    platformDefaultLimit={policy.platformDefaultLimit}
                    draft={overrideDrafts[tenant.tenantId]}
                    isBusy={isBusy}
                    isSaving={saveOverrideMutation.isPending && saveOverrideMutation.variables?.tenantId === tenant.tenantId}
                    onDraftChange={(value) => setOverrideDrafts((current) => ({ ...current, [tenant.tenantId]: value }))}
                    onSave={(limit) => saveOverrideMutation.mutate({ tenantId: tenant.tenantId, limit })}
                  />
                ))}
                {policy.tenants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                      No tenants found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {saveOverrideMutation.isError ? (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400" role="alert">
              {getErrorMessage(saveOverrideMutation.error)}
            </p>
          ) : null}
        </>
      ) : null}

      <p role="status" className="mt-3 min-h-4 text-xs text-green-700 dark:text-green-400">
        {statusMessage}
      </p>
    </section>
  );
}
